import { ButtonTypes } from '@genial/components';
import { ColorTypes } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';

import { useSuitabilityCheck } from '../../../../../../../../hooks/useSuitabilityCheck';
import {
  SignedInNavigationScreenNavigationProp,
  CustomerDetailStackParamList,
} from '../../../../../../../../routes/types';
import { AssetSelectedTypeMap } from '../../../../../../../../store/product/types';
import { ProductButtonProps } from '../../../../../../../BatchTicket/components/DynamicButton';
import { useCustomerDetailContext } from '../../../../../../context';
import { ResponsiveButton } from '../ResponsiveButton';

export interface ApplicationButtonProps<T extends keyof AssetSelectedTypeMap>
  extends ProductButtonProps<T> {
  tooltip?: string;
  idSelector: keyof AssetSelectedTypeMap[T];
  buttonText?: string;
  buttonColor?: ColorTypes.Option;
  buttonIcon?: IconographyTypes.Option;
  mobileButtonIconColor?: ColorTypes.Option;
  buttonVariant?: ButtonTypes.Variant;
  backgroundColor?: ColorTypes.Option;
}

export const ApplicationButton = <T extends keyof AssetSelectedTypeMap>({
  id,
  offer,
  tooltip,
  isDisabled,
  buttonText = 'Investir',
  buttonColor = 'neutral.white',
  mobileButtonIconColor = 'feedback.success.default',
  buttonIcon = 'Check',
  buttonVariant,
  backgroundColor,
  productContextConstant,
}: ApplicationButtonProps<T>) => {
  const { params } = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const { customer } = useCustomerDetailContext();
  const { ModalComponent, checkSuitability, isCheckingSuitability } = useSuitabilityCheck(
    params.accountNumber
  );

  const customerIsAbleToInvest =
    !!customer && !customer?.accountAndEarnings?.statusNormalized?.isBlocked;

  const handleSelectProduct = async () => {
    navigate('CustomerApplicationModal', {
      ...params,
      offer,
      productType: id,
    });
  };

  const handlePress = () => {
    checkSuitability(id, offer, handleSelectProduct);
  };

  const getTooltip = () => {
    if (!customerIsAbleToInvest) {
      return 'Este cliente possui bloqueios que impedem novos investimentos.';
    }
    return tooltip ?? '';
  };

  return (
    <>
      {ModalComponent}
      <ResponsiveButton
        text={buttonText}
        color={buttonColor}
        id={id}
        offer={offer}
        icon={buttonIcon}
        mobileIconColor={buttonColor}
        mobileButtonIconColor={mobileButtonIconColor}
        isDisabled={isDisabled || !customerIsAbleToInvest}
        isLoading={isCheckingSuitability}
        onPress={handlePress}
        buttonVariant={buttonVariant}
        backgroundColor={backgroundColor}
        tooltip={getTooltip()}
        productContextConstant={productContextConstant}
      />
    </>
  );
};
